import React from 'react';
import Dropzone, { FileRejection } from 'react-dropzone';
import { ServicesContext } from '~/services';
import { convertToBlob } from '~/util';
import { moment } from '~/vendorConfigs/moment';

const fileProperties = {
    maxSize: 48000000,
    acceptedTypes: ['.pdf'],
};

const totalMaxSize = 48000000;

interface UploadProps {
    patientId: number;
    gapType: number;
    disabled?: boolean;
    externalUpload?: ExternalAttachmentType | null;
    clearExternalUpload: () => void;
    mustConfirmUpload: boolean;
    rolePermission: boolean;
    totalUploadedSize?: number;
}

export function Upload({
    patientId,
    gapType,
    disabled = false,
    externalUpload = null,
    clearExternalUpload,
    mustConfirmUpload,
    rolePermission,
    totalUploadedSize = 0,
}: UploadProps) {
    const context = React.useContext(ServicesContext);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string[]>([]);

    function resetFileState() {
        setLoading(true);
        setError([]);
    }

    const performUpload = React.useCallback(
        async (files: File[], isMIC: boolean) => {
            setLoading(true);
            try {
                // The validator only checks one file at a time,
                // this catches if multiple files push us over
                if (
                    files.reduce((prev, file) => prev + file.size, totalUploadedSize) >=
                    totalMaxSize
                ) {
                    setError(['Total size of uploaded files cannot exceed 48 MB']);
                } else {
                    await context.patient.uploadMedia(patientId, gapType, files, isMIC);
                }
            } catch (e) {
                if (!e?.response?.data?.violations) {
                    setError(['An upload error occurred']);
                } else {
                    setError(e.response.data.violations.map((x: { message: string }) => x.message));
                }
            } finally {
                setLoading(false);
            }
        },
        [context.patient, gapType, patientId, totalUploadedSize]
    );

    function checkTotalUploadSize(file: File) {
        if (file.size + totalUploadedSize >= totalMaxSize) {
            return {
                code: 'total-too-large',
                message: 'Total size of uploaded files cannot exceed 48 MB',
            };
        }
        return null;
    }

    function fileReject(files: FileRejection[]) {
        setError(
            files
                .map((f) =>
                    f.errors.map((e) => {
                        if (e.code === 'file-too-large') {
                            return `File is larger than 48 MB (${f.file.name})`;
                        }
                        if (e.code === 'total-too-large') {
                            return e.message;
                        }
                        return `${e.message} (${f.file.name})`;
                    })
                )
                .flat()
        );
        setLoading(false);
    }

    React.useEffect(() => {
        if (!externalUpload || !externalUpload.document) return;
        // If the modal is already open, the external upload will reach here
        // before the confirmation modal gets swapped out, so wait for confirmation
        if (mustConfirmUpload) return;
        resetFileState();
        (async () => {
            const res = convertToBlob(externalUpload.document!);
            const filename = `${
                externalUpload.ehrOrigin ? `${externalUpload.ehrOrigin} ` : ''
            }Document ${moment().format('YYYY-MM-DD-ss')}.pdf`;
            const file = new File([res], filename, { type: 'application/pdf' });
            await performUpload([file], true);
            clearExternalUpload();
        })();
    }, [externalUpload, clearExternalUpload, performUpload, mustConfirmUpload]);

    return (
        <>
            <Dropzone
                accept={fileProperties.acceptedTypes}
                maxSize={fileProperties.maxSize}
                onDrop={resetFileState}
                onDropAccepted={(files) => performUpload(files, false)}
                onDropRejected={fileReject}
                validator={checkTotalUploadSize}
                disabled={!rolePermission || disabled}
            >
                {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps({ className: 'dropzone mb-3 patient-upload-dropzone' })}>
                        <input {...getInputProps()} aria-label="Patient Attachments" />
                        Drop files to attach, or click to browse
                    </div>
                )}
            </Dropzone>
            {error.length > 0 && (
                <div className="mb-2">
                    {error.map((e) => (
                        <div key={e} className="text-danger fw-bold">
                            {e}
                        </div>
                    ))}
                </div>
            )}
            {loading && (
                <div>
                    <i className="fa-solid fa-spin fa-spinner" />
                </div>
            )}
        </>
    );
}
